import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { urls } from 'app-constants';
import { useToggle } from 'hooks';
import { getCsrfToken, trackEvent } from 'utils';
import CardActionMenu from 'components/CardActionMenu';
import CardDeletionMenu from 'components/CardDeletionMenu';
import { statusOptions, statusColors } from './statusOptions';
import Icon from 'components/Icon';
import Modal from 'components/Modal';
import RadioList from 'components/RadioList';
import SubscriptionStatusBadge from 'components/SubscriptionStatusBadge';
import StatusBadge from './StatusBadge';

const Item = ({
  id,
  poi,
  status,
  billingContact,
  created,
  updated,
  auditEvents = [],
  subscription,
  className,
  metaLabel = 'created',
  onEditComplete = () => {},
  onDeleteSuccess = () => {},
  onDeleteFailure = () => {},
}) => {
  const dateCreated = parseISO(created);
  const dateUpdated = parseISO(updated);

  const [editModalOpen, toggleEditModal] = useToggle(false);
  const handleCardClick = () => toggleEditModal(true);
  const handleEditClick = () => toggleEditModal(true);
  const handleRequestClose = () => toggleEditModal(false);

  const [statusVal, setStatusVal] = useState(status);
  useEffect(() => setStatusVal(status), [status, editModalOpen]);

  const sendGtmEvent = statusVal => {
    const suffix = {
      verifying: 'start',
      verified: 'accept',
      rejected: 'reject',
    }[statusVal];

    if (suffix && poi) {
      trackEvent(`prod_verify_${suffix}`, {
        venueId: poi.id,
        venueName: poi.name,
      });
    }
  };

  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const handleSaveClick = () => {
    if (status === statusVal) {
      toggleEditModal(false);
      return null;
    }

    setIsSaving(true);
    setError(null);

    fetch(`${urls.poiClaimStatusUpdateBase}${id}/`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'X-CSRFToken': getCsrfToken(),
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status: statusVal, notes: notesVal }),
    })
      .then(response => {
        setIsSaving(false);

        if (response.status === 400) {
          // validation errors
          response.json().then(data => setError(data.detail));
        } else if (!response.ok) {
          throw new Error(response.statusText);
        } else {
          toggleEditModal(false);
          onEditComplete();
          sendGtmEvent(statusVal);
        }
        return response;
      })
      .catch(err => {
        setIsSaving(false);
        console.error(err);
      });
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const handleDeleteClick = () => {
    setIsDeleting(true);

    fetch(`${urls.poiClaimDeleteBase}${id}/`, {
      credentials: 'include',
      method: 'DELETE',
      headers: { 'X-CSRFToken': getCsrfToken() },
    })
      .then(response => {
        if (!response.ok) throw new Error(response.statusText);
        onDeleteSuccess();
      })
      .catch(err => {
        console.error(err);
        setIsDeleting(false);
        onDeleteFailure(err);
      });
  };

  const [showFullHistory, toggleShowFullHistory] = useToggle(false);
  const visibleAuditEvents = showFullHistory ? auditEvents : auditEvents.slice(0, 5);
  const displayShowAllLink = !showFullHistory && visibleAuditEvents.length < auditEvents.length;
  const handleShowAllClick = evt => {
    evt.preventDefault();
    toggleShowFullHistory();
  };
  useEffect(() => toggleShowFullHistory(false), [editModalOpen]);

  const [notesVal, setNotesVal] = useState('');
  const showNotesField = statusVal === 'rejected' && statusVal !== status;

  useEffect(() => {
    if (!showNotesField) setNotesVal('');
  }, [showNotesField]);

  const handleNotesChange = evt => setNotesVal(evt.target.value);

  const saveDisabled = isSaving || !poi || (showNotesField && !notesVal);

  const editModal = (
    <Modal
      isOpen={editModalOpen}
      onRequestClose={handleRequestClose}
      contentClassName="p-3 p-sm-6"
      ariaHideApp={false}
    >
      <h4 className="mb-3">Editing Venue Request</h4>
      <table className="table table-bordered">
        <tbody>
          <tr>
            <th>Venue</th>
            <td>
              {poi ? (
                <>
                  <a href={poi.absoluteUrl} target="_blank" rel="noopener noreferrer">{poi.name}</a>
                  {!poi.published && <em className="text-sm text-tertiary ms-2">unpublished</em>}
                </>
              ) : (
                <em className="text-tertiary">Venue missing</em>
              )}
            </td>
          </tr>
          <tr>
            <th>Contact</th>
            <td>{billingContact?.email}</td>
          </tr>
          <tr>
            <th>Status</th>
            <td>
              <RadioList
                itemStyle={{ padding: '5px 0' }}
                options={statusOptions}
                value={statusVal}
                onChange={setStatusVal}
              />
            </td>
          </tr>
        </tbody>
      </table>

      {showNotesField && (
        <>
          <h6 className="fw-bold">Explanation</h6>
          <div className="z-form-textarea-wrap mb-4">
            <textarea
              className="z-form-textarea"
              style={{ height: 150 }}
              value={notesVal}
              onChange={handleNotesChange}
            />
            <div className="z-form-hint-container" style={{ padding: 0 }}>
              Enter reason for rejection. This information will be shared with the user.
            </div>
          </div>
        </>
      )}

      <div className="mb-3">
        <h6 className="fw-bold">History</h6>
        {visibleAuditEvents.map(({ user, message, created }) => (
          <div key={created} className="py-2 border-bottom">
            <div>{message}</div>
            <div className="text-sm text-tertiary">{format(parseISO(created), 'yyyy-MM-dd p')}{!!user && ` – ${user.email}`}</div>
          </div>
        ))}
        {displayShowAllLink && <div className="mt-1"><a href="#show-all" onClick={handleShowAllClick}>show all</a></div>}
      </div>

      <div className="d-flex align-items-center justify-content-end">
        <a href="#cancel" className="small reset-link text-tertiary me-3" onClick={handleRequestClose}>Cancel</a>
        <button
          type="button"
          onClick={handleSaveClick}
          className="btn-z primary"
          disabled={saveDisabled}
        >
          Save
        </button>
      </div>
    </Modal>
  );

  return (
    <>
      <div
        className={classNames('listings-card', 'saved-search-card', className)}
        style={{ opacity: isDeleting ? 0.5 : 1 }}
        onClick={handleCardClick}
      >
        <section className="lc-top">
          <div className={classNames('lc-main p-0')}>
            <div>
              <div className="d-flex align-items-baseline">
                {poi ? (
                  <>
                    <h5 className={classNames('mb-2', 'saved-search-card-title')}>
                      <a
                        href={poi.absoluteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                      >{poi.name}</a>
                    </h5>
                    {!poi.published && <em className="text-sm text-tertiary ms-2">unpublished</em>}
                  </>
                ) : (
                  <h5 className={classNames('mb-2', 'saved-search-card-title text-tertiary')}>
                    <em>Venue missing</em>
                  </h5>
                )}
              </div>

              <div className="mb-2 lh-sm d-flex align-items-center">
                <div className="me-2 d-flex justify-content-center" style={{ flex: '0 0 20px', color: '#cacaca' }}>
                  <Icon i="user" size="sm" />
                </div>
                <div>
                  <strong>{billingContact.email}</strong>
                </div>
              </div>

              {!!subscription && (
                <div className="mb-2 lh-sm d-flex align-items-center">
                  <SubscriptionStatusBadge status={subscription.status} />
                  <div className="ms-2 text-tertiary">
                    {subscription.plan}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lc-right" style={{ flexDirection: 'row', alignItems: 'start' }}>
            <StatusBadge
              status={status}
              className="d-block mt-1 me-2"
            />
            <div onClick={e => e.stopPropagation()}>
              {status === 'verified' ? (
                <CardActionMenu items={[
                  { label: 'Edit', onClick: handleEditClick }
                ]} />
              ) : (
                <CardDeletionMenu
                  extraItems={[
                    { label: 'Edit', onClick: handleEditClick }
                  ]}
                  onDeleteClick={handleDeleteClick}
                />
              )}
            </div>
          </div>
        </section>

        <div className="saved-search-card-meta">
          {metaLabel === 'created' && `Created ${formatDistanceToNow(dateCreated)} ago`}
          {metaLabel === 'updated' && `Updated  ${formatDistanceToNow(dateUpdated)} ago`}
        </div>
      </div>
      {editModal}
    </>
  );
};

const userPropTypes = PropTypes.shape({
  id: PropTypes.number,
  username: PropTypes.string,
  email: PropTypes.string,
});

Item.propTypes = {
  className: PropTypes.string,
  id: PropTypes.number,
  poi: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    absoluteUrl: PropTypes.string,
    published: PropTypes.bool,
  }),
  status: PropTypes.oneOf(statusOptions),
  billingContact: userPropTypes,
  auditEvents: PropTypes.arrayOf(PropTypes.shape({
    user: userPropTypes,
    message: PropTypes.string,
    created: PropTypes.string,
  })),
  subscription: PropTypes.shape({
    status: PropTypes.string,
    plan: PropTypes.string,
  }),
  created: PropTypes.string,
  updated: PropTypes.string,
  metaLabel: PropTypes.oneOf(['updated', 'created']),
  onEditComplete: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
  onDeleteFailure: PropTypes.func,
};

export default Item;
